import { useEffect } from 'react';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';

import ShipFromName from '../../../../../../../ship-from/components/ship-from-name';
import { useShipFrom } from '../../../../../../../hooks/ship-froms/use-ship-from';
import { useBuildInitialShipFrom } from '../../../../../../../ship-from/hooks/use-build-initial-ship-from';
import { useInventorySources } from '../../../../../../../ship-from/hooks/use-inventory-sources';
import Errors from '../../../../../../../ship-from/components/errors';
import { ShipFromContextProvider, useShipFromContext } from '../../../../../../../ship-from/contexts/ship-from-context';
import { CustomOriginAddress } from './custom-origin-address';
import ReturnAddress from '../../../../../../../ship-from/components/return-address';
import { CustomResidentialCheckbox } from './custom-residential-checkbox';
import { ShipFromTrackingSources } from '../../../../../../../ship-from/tracking-events';

import messages from '../messages';
import style from './style.module.less';
import { useOnComponentMount } from '../../../../../../../hooks/use-on-component-mount';
import { FlexColumn } from 'ui-components/components/flex';

/**
 * Form component for creating or editing a ship from location
 * @param {object} props - Component props
 * @param {() => void} props.onSuccess - Callback function called when the form is successfully submitted
 * @param {(error: Error) => void} props.onError - Callback function called when there's an error with the form submission
 * @param {string|number} [props.shipFromId] - ID of an existing ship from location to edit. If not provided, a new ship from location will be created
 * @param {(isValid: boolean) => void} [props.onValidationChange] - Callback function called when validation state changes
 * @param {(saveFunction: () => Promise<void>) => void} [props.onSaveFunctionReady] - Callback to expose save function
 * @returns {React.ReactElement} The ShipFromForm component
 */
export const ShipFromForm = ({ onSuccess, onError, shipFromId, onValidationChange, onSaveFunctionReady }) => {
  const { fetch, isLoading, sources } = useInventorySources();

  useOnComponentMount(() => {
    fetch();
  }, [fetch]);

  const initialShipFrom = useBuildInitialShipFrom(sources);

  // If editing an existing ship from, use it; otherwise use the initial ship from
  const shipFrom = useShipFrom(shipFromId) ?? initialShipFrom;

  // Set inventory source to 'none'
  const shipFromWithNoneInventory = {
    ...shipFrom,
    inventorySource: 'none',
  };

  if (isLoading) {
    return <LoadingIndicator className={style['loading-indicator']} />;
  }

  return (
    <ShipFromContextProvider
      onError={onError}
      onSuccess={onSuccess}
      shipFrom={shipFromWithNoneInventory}
      shouldCreateInventoryWarehouse
      source={ShipFromTrackingSources.FTU_WIZARD_SHIP_FROM_STEP}
      sources={sources}
    >
      <ValidationChangeHandler onSaveFunctionReady={onSaveFunctionReady} onValidationChange={onValidationChange} />
      <FlexColumn className={style['ship-from-form']}>
        <Errors />
        <div className={style['form-header']}>
          <Text className={style['form-title']} textType={TextType.H2} value={messages.shipFromLocationsTitle} />
          <Text textType={TextType.Description} value={messages.shipFromLocationsDescription} />
        </div>
        <div className={style['form-columns']}>
          <div className={style['left-column']}>
            <Text className={style['address-form-headers']} textType={TextType.H3} value={messages.addressDetails} />
            <ShipFromName />
            <div className={style['custom-origin-address']}>
              <CustomOriginAddress />
            </div>
          </div>
          <div className={style['right-column']}>
            <FlexColumn className={style['settings-checkboxes']}>
              <CustomResidentialCheckbox />
              <ReturnAddress />
            </FlexColumn>
          </div>
        </div>
      </FlexColumn>
    </ShipFromContextProvider>
  );
};

/**
 * Component that monitors validation state changes and notifies parent
 * @param {object} props - Component props
 * @param {(isValid: boolean) => void} [props.onValidationChange] - Callback for validation changes
 * @param {(saveFunction: () => Promise<void>) => void} [props.onSaveFunctionReady] - Callback to expose save function
 * @returns {null} This component doesn't render anything
 */
const ValidationChangeHandler = ({ onValidationChange, onSaveFunctionReady }) => {
  const { isValid, onSave } = useShipFromContext();

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  useEffect(() => {
    if (onSaveFunctionReady && onSave) {
      onSaveFunctionReady(onSave);
    }
  }, [onSaveFunctionReady, onSave]);

  return null;
};
